import { useEffect } from "react"
import { useLocation } from "react-router-dom"

/**
 * Per-route SEO / GEO head management.
 *
 * Site-wide entities (WebSite, Person/ProfessionalService) live in public/index.html.
 * This component adds page-level data for every route: title, description, canonical,
 * robots, Open Graph / Twitter, and a JSON-LD @graph with WebPage + BreadcrumbList
 * plus any page-specific entities passed via `jsonLd`.
 *
 * react-snap runs effects before snapshotting, so everything set here ends up in the
 * prerendered HTML and is visible to crawlers without JavaScript.
 */

export const BASE_URL = "https://puhony.eu"
export const SITE_NAME = "Matěj Půhoný"
export const PERSON_ID = `${BASE_URL}/#person`
export const WEBSITE_ID = `${BASE_URL}/#website`
const DEFAULT_IMAGE = `${BASE_URL}/img/og.png`

type JsonLd = Record<string, any>

interface SEOProps {
    title: string
    description: string
    /** Canonical path override (e.g. "/reference/" for the legacy /portfolio alias). Defaults to the current pathname. */
    path?: string
    /** Short label used in the breadcrumb trail. Pass `false` to omit the BreadcrumbList. Defaults to `title`. */
    breadcrumb?: string | false
    /** schema.org type of the WebPage node (WebPage, CollectionPage, ContactPage, ProfilePage, AboutPage, ...). */
    pageType?: string
    /** Open Graph type. */
    ogType?: "website" | "profile" | "article"
    /** Absolute URL of the social share image. */
    image?: string
    noindex?: boolean
    /** Extra JSON-LD node(s) merged into the page @graph. */
    jsonLd?: JsonLd | JsonLd[]
}

/** Normalise a route path to the trailing-slash form the static host serves ("/sluzby" -> "/sluzby/"). */
export const canonicalPath = (pathname: string) => {
    const clean = pathname.replace(/\/+$/, "")
    if (clean === "") return "/"
    // Leave file-like paths (e.g. react-snap's /404.html) alone.
    if (/\.[a-z0-9]+$/i.test(clean)) return clean
    return `${clean}/`
}

export const absoluteUrl = (pathname: string) => `${BASE_URL}${canonicalPath(pathname)}`

const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
    let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
    if (!el) {
        el = document.createElement("meta")
        el.setAttribute(attr, key)
        document.head.appendChild(el)
    }
    el.setAttribute("content", content)
}

const upsertLink = (rel: string, href: string) => {
    let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
    if (!el) {
        el = document.createElement("link")
        el.setAttribute("rel", rel)
        document.head.appendChild(el)
    }
    el.setAttribute("href", href)
}

const SEO = ({
    title,
    description,
    path,
    breadcrumb,
    pageType = "WebPage",
    ogType = "website",
    image = DEFAULT_IMAGE,
    noindex = false,
    jsonLd,
}: SEOProps) => {
    const location = useLocation()
    const pagePath = canonicalPath(path ?? location.pathname)
    const fullUrl = `${BASE_URL}${pagePath}`
    const isHome = pagePath === "/"
    const fullTitle = isHome ? `${SITE_NAME} — ${title}` : `${title} | ${SITE_NAME}`
    const crumbLabel = breadcrumb === undefined ? title : breadcrumb

    const graph: JsonLd[] = []

    const webPage: JsonLd = {
        "@type": pageType,
        "@id": `${fullUrl}#webpage`,
        "url": fullUrl,
        "name": fullTitle,
        "description": description,
        "inLanguage": "cs",
        "isPartOf": { "@id": WEBSITE_ID },
        "about": { "@id": PERSON_ID },
        "primaryImageOfPage": { "@type": "ImageObject", "url": image },
    }
    if (isHome) {
        webPage.speakable = {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", "main p"],
        }
    }
    if (crumbLabel !== false && !isHome) {
        webPage.breadcrumb = { "@id": `${fullUrl}#breadcrumb` }
        graph.push({
            "@type": "BreadcrumbList",
            "@id": `${fullUrl}#breadcrumb`,
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Úvod", "item": `${BASE_URL}/` },
                { "@type": "ListItem", "position": 2, "name": crumbLabel, "item": fullUrl },
            ],
        })
    }
    graph.unshift(webPage)

    if (jsonLd) {
        const extra = Array.isArray(jsonLd) ? jsonLd : [jsonLd]
        extra.forEach(node => {
            const rest: JsonLd = { ...node }
            delete rest["@context"]
            // A node addressing this page's WebPage @id extends the auto-generated node instead of duplicating it.
            if (rest["@id"] === webPage["@id"]) {
                const { "@type": extraType, ...fields } = rest
                Object.assign(webPage, fields)
                if (extraType && pageType === "WebPage") webPage["@type"] = extraType
                return
            }
            graph.push(rest)
        })
    }

    const jsonLdString = JSON.stringify({ "@context": "https://schema.org", "@graph": graph })

    useEffect(() => {
        document.title = fullTitle

        upsertMeta("name", "title", fullTitle)
        upsertMeta("name", "description", description)
        upsertMeta(
            "name",
            "robots",
            noindex
                ? "noindex, nofollow"
                : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        )

        upsertMeta("property", "og:type", ogType)
        upsertMeta("property", "og:title", fullTitle)
        upsertMeta("property", "og:description", description)
        upsertMeta("property", "og:url", fullUrl)
        upsertMeta("property", "og:image", image)
        upsertMeta("property", "og:image:secure_url", image)

        upsertMeta("name", "twitter:title", fullTitle)
        upsertMeta("name", "twitter:description", description)
        upsertMeta("name", "twitter:url", fullUrl)
        upsertMeta("name", "twitter:image", image)

        upsertLink("canonical", fullUrl)

        let script = document.getElementById("page-jsonld") as HTMLScriptElement | null
        if (!script) {
            script = document.createElement("script")
            script.id = "page-jsonld"
            script.type = "application/ld+json"
            document.head.appendChild(script)
        }
        script.textContent = jsonLdString

        return () => {
            script?.remove()
        }
    }, [fullTitle, description, fullUrl, image, ogType, noindex, jsonLdString])

    return null
}

export default SEO
